import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Stack,
  Select, MenuItem, FormControl, InputLabel, TextField,
} from '@mui/material';
import { initKeycloak } from '../keycloak';
import { commercialGet } from '../utils/commercialApi';
import { pendingCounts } from '../utils/adminPendingApi';
import PendingBadge from './admin/PendingBadge';
import CommercialKPIs from './commercial/CommercialKPIs';
import GoalProgress from './commercial/GoalProgress';
import CommercialFunnel from './commercial/CommercialFunnel';
import ZonePerformance from './commercial/ZonePerformance';
import CommercialMap from './commercial/CommercialMap';
import AgentPerformance from './commercial/AgentPerformance';
import AcquisitionChannels from './commercial/AcquisitionChannels';
import MRRChart from './commercial/MRRChart';

const ZONES = ['Paris 12', 'Beaune', 'Guadeloupe'];

// Mirrors MainBoard.jsx's auth+fetch+layout conventions (same
// initKeycloak() flow, same plain-fetch pattern, same Container/Paper
// page shape) rather than introducing a different style for this one
// page - see utils/commercialApi.js and utils/commercialColors.js for
// the pieces factored out because this page has several sub-widgets
// instead of one big component.
export default function CommercialDashboard() {
  const [kcInstance, setKcInstance] = useState(null);
  const [token, setToken] = useState('');
  const [pending, setPending] = useState(null); // the red badges on the admin links (routes/adminPending.ts)
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const loginRequestedRef = useRef(false);

  const [zone, setZone] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [zones, setZones] = useState(null);
  const [agents, setAgents] = useState(null);
  const [channels, setChannels] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [facilities, setFacilities] = useState(null);
  const [error, setError] = useState('');

  /* ─── Auth ─── */
  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      window.history.replaceState(null, '', window.location.origin + window.location.pathname);

      if (!kc.authenticated) {
        if (loginRequestedRef.current) return;
        loginRequestedRef.current = true;
        kc.login({ redirectUri: `${window.location.origin}/admin/commercial` });
        return;
      }

      // Every route this page calls is already ROLE_ADMIN-or-ROLE_SALES
      // gated server side (see backend/index.ts's commercial predicate
      // guard) - this is defense-in-depth, not the actual boundary.
      if (!kc.hasRealmRole('ROLE_ADMIN') && !kc.hasRealmRole('ROLE_SALES')) {
        setForbidden(true);
        setLoading(false);
        return;
      }

      setKcInstance(kc);
      setToken(kc.token ?? '');
    })().catch(console.error);
  }, []);

  /* ─── What waits behind the prospects and visits links (admins only) ─── */
  useEffect(() => {
    if (!token || !kcInstance?.hasRealmRole('ROLE_ADMIN')) return;
    pendingCounts(token).then((r) => setPending(r.commercial)).catch(() => {});
  }, [token, kcInstance]);

  /* ─── Fetch dashboard data ─── */
  const loadData = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError('');

    const periodFilter = { zone: zone || undefined, agent: agentFilter || undefined, from: from || undefined, to: to || undefined };

    Promise.all([
      commercialGet('/overview', token, periodFilter),
      commercialGet('/funnel', token, periodFilter),
      commercialGet('/zones', token),
      commercialGet('/agents', token),
      commercialGet('/channels', token),
      commercialGet('/mrr', token, { zone: zone || undefined, agent: agentFilter || undefined, from: from || undefined, to: to || undefined }),
      commercialGet('/facilities', token, { zone: zone || undefined, agent: agentFilter || undefined }),
    ])
      .then(([ov, fn, zn, ag, ch, mr, fc]) => {
        setOverview(ov);
        setFunnel(fn);
        setZones(zn.zones);
        setAgents(ag.agents);
        setChannels(ch.channels);
        setMrr(mr);
        setFacilities(fc.facilities);
      })
      .catch((err) => setError(err.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [token, zone, agentFilter, from, to]);

  useEffect(() => { loadData(); }, [loadData]);

  if (forbidden) {
    return (
      <Typography sx={{ mt: 4, textAlign: 'center' }}>
        Accès refusé.
      </Typography>
    );
  }

  if (loading && !overview) {
    return (
      <Typography sx={{ mt: 4, textAlign: 'center' }}>
        Chargement…
      </Typography>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Dashboard Commercial</Typography>
        <Stack direction="row" spacing={1.5}>
          {kcInstance?.hasRealmRole('ROLE_ADMIN') && (
            <Button
              variant="text"
              component={RouterLink}
              to="/admin/commercial/agents"
            >
              👥 Ambassadeurs
            </Button>
          )}
          {kcInstance?.hasRealmRole('ROLE_ADMIN') && (
            <Button
              variant="text"
              component={RouterLink}
              to="/admin/commercial/leads"
            >
              🔗 Attribuer les leads
            </Button>
          )}
          {kcInstance?.hasRealmRole('ROLE_ADMIN') && (
            <Button variant="text" component={RouterLink} to="/admin/acquisition">
              📈 Google Ads
            </Button>
          )}
          {kcInstance?.hasRealmRole('ROLE_ADMIN') && (
            <>
              <Button variant="text" component={RouterLink} to="/admin/commercial/ambassador/territories">
                🗺️ Territoires
              </Button>
              <PendingBadge count={pending?.prospects}>
                <Button variant="text" component={RouterLink} to="/admin/commercial/ambassador/prospects">
                  📍 Prospects
                </Button>
              </PendingBadge>
              <PendingBadge count={pending?.visits}>
                <Button variant="text" component={RouterLink} to="/admin/commercial/ambassador/visits">
                  ✅ Visites à vérifier
                </Button>
              </PendingBadge>
              <Button variant="text" component={RouterLink} to="/admin/commercial/ambassador/settings">
                ⚙️ Règles
              </Button>
            </>
          )}
          <Button variant="contained" component={RouterLink} to="/ambassador">
            📱 Espace terrain
          </Button>
          <Button
            variant="text"
            component={RouterLink}
            to="/admin/commercial/docs"
          >
            📖 Documentation
          </Button>
          <Button
            variant="outlined"
            onClick={() => kcInstance?.logout({ redirectUri: `${window.location.origin}/` })}
          >
            Logout
          </Button>
        </Stack>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Zone</InputLabel>
          <Select value={zone} label="Zone" onChange={(e) => setZone(e.target.value)}>
            <MenuItem value="">Toutes</MenuItem>
            {ZONES.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Commercial</InputLabel>
          <Select value={agentFilter} label="Commercial" onChange={(e) => setAgentFilter(e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            {(agents || []).map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" type="date" label="Du" InputLabelProps={{ shrink: true }}
          value={from} onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          size="small" type="date" label="Au" InputLabelProps={{ shrink: true }}
          value={to} onChange={(e) => setTo(e.target.value)}
        />
      </Stack>

      {error && <Typography sx={{ color: '#d03b3b', mb: 2 }}>{error}</Typography>}

      <Stack spacing={3}>
        <CommercialKPIs overview={overview} />
        <GoalProgress overview={overview} />
        <CommercialFunnel funnel={funnel} />
        <ZonePerformance zones={zones} onZoneClick={setZone} />
        <CommercialMap facilities={facilities} />
        <AgentPerformance agents={agents} />
        <AcquisitionChannels channels={channels} />
        <MRRChart mrr={mrr} />
      </Stack>
    </Container>
  );
}
